import { useCallback, useEffect, useState } from 'react';

import { Option } from '../OptionsList/OptionsList';
import { getDevices } from '../../../features/Media/services/media';
import { useMediaContext } from '../../../features/Media/providers/MediaContextProvider';
import { publish } from '../../../features/EventsBus';
import { MediaDomainEvents } from '../../../features/Media/domain/events';

export function useAudioOutputOptions(): {
	opened: boolean;
	selected?: string;
	options: Option[];
	open: () => void;
	close: () => void;
	} {
	const [options, setOptions] = useState<Option[]>([]);
	const [opened, setOpened] = useState(false);
	const [selected, setSelected] = useState<string | undefined>(undefined);

	const { selectedAudioDevice } = useMediaContext();

	const loadOptions = async (): Promise<Option[]> => {
		const devices = await getDevices();

		if (devices?.audiooutput.length && !selectedAudioDevice) {
			setSelected(devices.audiooutput[0].deviceId);
		}

		return devices?.audiooutput.length
			? devices.audiooutput.map((device) => {
				if (selectedAudioDevice) {
					setSelected(selectedAudioDevice);
				} else if (device.deviceId === 'default') {
					setSelected(device.deviceId);
				}

				return {
					key: device.deviceId,
					label: device.label,
					onClick: () => {
						onSelectedDevice(device);
					},
				};
			  })
			: [];
	};

	const onSelectedDevice = useCallback((device: MediaDeviceInfo) => {
		if (!device?.deviceId) {
			return;
		}

		publish(
			MediaDomainEvents.changeAudioOutputDevice, {
				deviceId: device.deviceId,
			}
		);
	}, []);

	useEffect(() => {
		if (selectedAudioDevice) {
			setSelected(selectedAudioDevice);
		}
	}, [selectedAudioDevice]);

	useEffect(() => {
		loadOptions()
			.then((devices) => {
				setOptions(devices);
			})
			.catch(() => {
				publish(MediaDomainEvents.cannotListAudioOutputDevices);
			});
	}, []);

	const open = useCallback(() => {
		setOpened(true);
	}, []);

	const close = useCallback(() => {
		setOpened(false);
	}, []);

	return {
		options,
		opened,
		selected,
		open,
		close,
	};
}
